import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  IconButton,
  Divider,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuidv4 } from 'uuid';
import { updateServiceOffering, addServiceOffering, deleteServiceOffering } from '../../services/providerService';
import { useAuth } from '../../contexts/AuthContext';

export default function ServiceOfferingsPanel({ settings, setSettings }) {
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const { currentUser } = useAuth();

  const initialOfferingState = {
    id: '',
    name: '',
    description: '',
    job_type: '',
    base_price: 0,
    price_per_sqft: 0,
    minimum_price: 0,
    materials_included: false,
    estimated_hours_per_sqft: 0.1
  };

  const [formData, setFormData] = useState(initialOfferingState);

  // Function to open dialog for editing an existing offering
  const handleOpenDialog = (offering) => {
    if (offering) {
      // Edit existing offering
      setFormData({
        ...offering,
        base_price: parseFloat(offering.base_price) || 0,
        price_per_sqft: parseFloat(offering.price_per_sqft) || 0,
        minimum_price: parseFloat(offering.minimum_price) || 0,
        estimated_hours_per_sqft: parseFloat(offering.estimated_hours_per_sqft) || 0.1
      });
      setIsEditing(true);
    } else {
      // Add new offering
      setFormData({
        ...initialOfferingState,
        id: uuidv4(),
        base_price: 0,
        price_per_sqft: 0,
        minimum_price: 0,
        estimated_hours_per_sqft: 0.1
      });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData(initialOfferingState);
  };

  // Handle input changes for form fields
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'number') {
      const numValue = value === '' ? 0 : parseFloat(value);
      setFormData({
        ...formData,
        [name]: isNaN(numValue) ? 0 : numValue
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      if (isEditing) {
        await updateServiceOffering(currentUser.uid, formData.id, formData);
        
        // Update local state
        const updatedOfferings = settings.service_offerings.map(offering => 
          offering.id === formData.id ? formData : offering
        );
        
        setSettings({
          ...settings,
          service_offerings: updatedOfferings
        });
        
        setSnackbar({
          open: true,
          message: 'Service updated successfully',
          severity: 'success'
        });
      } else {
        await addServiceOffering(currentUser.uid, formData);
        
        // Update local state
        setSettings({
          ...settings,
          service_offerings: [...settings.service_offerings, formData]
        });
        
        setSnackbar({
          open: true,
          message: 'Service added successfully',
          severity: 'success'
        });
      }
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving service offering:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save service. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (offeringId) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        setLoading(true);
        await deleteServiceOffering(currentUser.uid, offeringId);
        
        // Update local state
        const updatedOfferings = settings.service_offerings.filter(
          offering => offering.id !== offeringId
        );
        
        setSettings({
          ...settings,
          service_offerings: updatedOfferings
        });
        
        setSnackbar({
          open: true,
          message: 'Service deleted successfully',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting service offering:', error);
        setSnackbar({
          open: true,
          message: 'Failed to delete service. Please try again.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Service Offerings
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add New Service
        </Button>
      </Box>

      {settings.service_offerings.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          You haven't added any services yet. Click "Add New Service" to get started.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {settings.service_offerings.map((offering) => (
            <Grid item xs={12} md={6} lg={4} key={offering.id}>
              <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h3" gutterBottom>
                    {offering.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {offering.description}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Service Type: {offering.job_type}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Base Price:</strong> ${offering.base_price.toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Price per sq ft:</strong> ${offering.price_per_sqft.toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Minimum Price:</strong> ${offering.minimum_price.toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Materials Included:</strong> {offering.materials_included ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Est. Hours per sq ft:</strong> {offering.estimated_hours_per_sqft.toFixed(2)}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  <IconButton 
                    color="primary" 
                    onClick={() => handleOpenDialog(offering)}
                    aria-label="edit service"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    color="error" 
                    onClick={() => handleDelete(offering.id)}
                    aria-label="delete service"
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Service Dialog Form */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Service' : 'Add New Service'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Service Name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="job_type"
                label="Service Type"
                value={formData.job_type}
                onChange={handleInputChange}
                fullWidth
                required
                helperText="E.g., painting, plumbing, electrical"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="base_price"
                label="Base Price ($)"
                type="number"
                value={formData.base_price}
                onChange={handleInputChange}
                fullWidth
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="price_per_sqft"
                label="Price per sq ft ($)"
                type="number"
                value={formData.price_per_sqft}
                onChange={handleInputChange}
                fullWidth
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="minimum_price"
                label="Minimum Price ($)"
                type="number"
                value={formData.minimum_price}
                onChange={handleInputChange}
                fullWidth
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="estimated_hours_per_sqft"
                label="Est. Hours per sq ft"
                type="number"
                value={formData.estimated_hours_per_sqft}
                onChange={handleInputChange}
                fullWidth
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    name="materials_included"
                    checked={formData.materials_included}
                    onChange={handleInputChange}
                    color="primary"
                  />
                }
                label="Materials Included"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
